import React from 'react';
import { CollectTrackingResultType } from '../../../enums/CollectTrackingResultType';
import { CollectTracking } from '../../../models/CollectTracking';
import { ErpOrder } from '../../../models/ErpOrder';
import { ErpNfe } from '../../../models/ErpNfe';
import { Carrier } from '../../../models/Carrier';
import { CarrierStatusTracking } from '../../../models/CarrierStatusTracking';
import { Fulfillment } from '../../../models/Fulfillment';
import { ManageTransportHelper } from '../../../helpers/ManageTransportHelper';
import { GetOrderAddressesOutput } from '../../../services/Orders/GetOrderAddresses/GetOrderAddressesOutput';
import { Button } from '../../../common-components/Button/Button';
import { CardCollectTrackingItem } from '../CardCollectTrackingItem/CardCollectTrackingItem';
import { Container, OptionsContainer, StatusContainer, StatusDetails, StatusText } from './styles';
import { useAuth } from '../../../contexts/AuthContext';
import { SessionHelper } from '../../../helpers/SessionHelper';

interface ICardCollectTrackingProps {
  orderNumber?: string;
  shippingLineCode?: string;
  collectTracking: CollectTracking;
  nfes: ErpNfe[];
  carrier?: Carrier;
  erpOrderList?: ErpOrder[];
  orderAddresses?: GetOrderAddressesOutput|null;
  carrierStatusTrackingList?: CarrierStatusTracking[];
  manuallyCollect?: (getNfe: boolean) => void;
  changeTrackingCode?: (collectTrackingItemId: number, trackingCode: string) => void;
  fulfillments?: Fulfillment[];
}

const CardCollectTracking: React.FC<ICardCollectTrackingProps> = ({
  orderNumber,
  shippingLineCode,
  collectTracking,
  nfes,
  carrier,
  erpOrderList,
  orderAddresses,
  carrierStatusTrackingList,
  manuallyCollect,
  changeTrackingCode,
  fulfillments
}) => {

  const { session } = useAuth();
  const userCanManageTransport = SessionHelper.canManageTransport(session);

  const status = ManageTransportHelper.getCollectTrackingStatus(collectTracking);

  const memoPendigUserAndFailedOptions = React.useMemo(() => {
    const showOptions = (
      (carrier && carrier.trackable) && 
      (
        (collectTracking.collectTrackingResultType === CollectTrackingResultType.PendingUser || collectTracking.collectTrackingResultType === CollectTrackingResultType.Failed) ||
        (!collectTracking.collectTrackingItems || collectTracking.collectTrackingItems.length === 0)
      )
    );
    
    if (!showOptions || !userCanManageTransport) return null;

    const noErpOrder = !erpOrderList || erpOrderList.length === 0;
    const allNFeCancelled = erpOrderList && erpOrderList.length > 0 && 
      (nfes.length === 0 || nfes.every(nfe => nfe.isCancelled));
    const allNFeInvalid = erpOrderList && erpOrderList.length > 0 && 
      (nfes.length === 0 || nfes.every(nfe => nfe.keyNfe && nfe.keyNfe.length > 0 && !nfe.isCancelled ? false : true));

    const pendigUserAndFailedOptions = (noErpOrder || allNFeCancelled || allNFeInvalid) ? (
      <Button text='Obter Nota Fiscal e Realizar Coleta Virtual' click={() => manuallyCollect && manuallyCollect(true)} />
    ) : (
      <Button text='Refazer Coleta Virtual' click={() => manuallyCollect && manuallyCollect(false)} />
    );

    return (
      <OptionsContainer>
        {pendigUserAndFailedOptions}
      </OptionsContainer>
    )

  }, [carrier, collectTracking, erpOrderList, nfes]);

  const memoSedexTrackingView = React.useMemo(() => {
    const carrierIsSedex = carrier?.name?.toLowerCase()?.includes('sedex');
    if (!carrierIsSedex) return null;
    const orderHasFulfillment = fulfillments && fulfillments.length > 0;
    if (!orderHasFulfillment) {
      return (
        <div style={{color: '#bd880b', marginTop:'10px'}}>
          Pedido sedex ainda não enviado.
        </div>
      )
    }

    const firstFulfillmentTrackingNumber = fulfillments[0].trackingNumber;
    if (!firstFulfillmentTrackingNumber) {
      return (
        <div style={{color: '#bd880b', marginTop:'10px'}}>
          Pedido enviado por sedex ainda não possui número de rastreio.
        </div>
      )
    }

    return (
      <div>
        <div style={{marginTop:'10px'}}>
          <span style={{color:'#43853c', marginRight:'5px'}}>Código de rastreio</span>
          <a href={`https://rastreamento.correios.com.br/app/index.php?objetos=${firstFulfillmentTrackingNumber}`} target="_blank" rel="noopener noreferrer">
            {firstFulfillmentTrackingNumber}
          </a>
        </div>
      </div>
    )
  }, [carrier, fulfillments]);

  return (
    <Container key={`collect-tracking-${collectTracking.id}`}>
      <StatusContainer>
        <StatusText>{status.text}</StatusText>
        {status.details && <StatusDetails>{status.details}</StatusDetails>}
      </StatusContainer>
      {collectTracking.collectTrackingItems?.map(collectTrackingItem => {
        const erpOrder = erpOrderList?.find(erpol => erpol.erpNfes.some(nfe => nfe.id === collectTrackingItem.erpNfeId));
        const erpNFe = erpOrder?.erpNfes.find(nfe => nfe.id === collectTrackingItem.erpNfeId);
        return (
          <CardCollectTrackingItem
            key={`collect-tracking-item-${collectTrackingItem.id}`}
            orderNumber={orderNumber}
            shippingLineCode={shippingLineCode}
            collectTrackingItem={collectTrackingItem}
            erpNFe={erpNFe}
            orderAddresses={orderAddresses}
            carrierStatusTrackingList={carrierStatusTrackingList}
            changeTrackingCode={changeTrackingCode}
          />
        )
      })}
      {memoPendigUserAndFailedOptions}
      {memoSedexTrackingView}
    </Container>
  );
    
}

export {CardCollectTracking};